/**
 * textures.js — canvas-painted textures for the carnival.
 *
 * No image files ship with this game. Every pattern, label and bit of signage
 * typography is painted into a 2D canvas at load and uploaded once. Textures
 * are cached by key, so twelve booths share one stripe texture and forty cans
 * share one label.
 *
 * Sizes stay at 256–512 px. These are read from 1–10 m away on a mobile GPU;
 * anything larger is memory spent on detail nobody sees.
 *
 * @module assets3d/textures
 */

import * as THREE from 'three';
import { ART, CSS, css, rngFor } from './palette.js';

const cache = new Map();

/**
 * Build (or fetch) a cached canvas texture.
 *
 * @param {string} key Cache key. Must encode every parameter that affects pixels.
 * @param {number} w
 * @param {number} h
 * @param {(ctx: CanvasRenderingContext2D, w: number, h: number) => void} draw
 * @param {{ repeat?: [number, number], wrap?: THREE.Wrapping, data?: boolean }} [opts]
 *   `data: true` marks the texture as non-colour (alpha/roughness maps).
 * @returns {THREE.CanvasTexture}
 */
export function paint(key, w, h, draw, opts = {}) {
  const full = `${key}|${w}x${h}|${opts.repeat || ''}|${opts.wrap || ''}`;
  const hit = cache.get(full);
  if (hit) return hit;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  draw(ctx, w, h);

  const tex = new THREE.CanvasTexture(canvas);
  if (!opts.data) tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const wrap = opts.wrap ?? THREE.RepeatWrapping;
  tex.wrapS = wrap;
  tex.wrapT = wrap;
  if (opts.repeat) tex.repeat.set(opts.repeat[0], opts.repeat[1]);
  cache.set(full, tex);
  return tex;
}

/** The chunky carnival display face. Canvas fonts only — nothing to download. */
export const CARNIVAL_FONT = '"Bookman Old Style", "Georgia", "Times New Roman", serif';

/**
 * Draw text with a carnival showcard treatment: drop shadow, dark outline,
 * fill, then a highlight along the top edge of the letterforms.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x
 * @param {number} y
 * @param {{ size: number, fill?: string, outline?: string, highlight?: string,
 *           weight?: string, shadow?: boolean, maxWidth?: number }} o
 */
export function showcardText(ctx, text, x, y, o) {
  const weight = o.weight ?? 'bold';
  ctx.save();
  ctx.font = `${weight} ${o.size}px ${CARNIVAL_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const width = o.maxWidth;
  const put = (fn, dx = 0, dy = 0) => {
    if (width) fn.call(ctx, text, x + dx, y + dy, width);
    else fn.call(ctx, text, x + dx, y + dy);
  };

  if (o.shadow !== false) {
    ctx.fillStyle = 'rgba(0,0,0,0.32)';
    put(ctx.fillText, o.size * 0.055, o.size * 0.07);
  }
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(3, o.size * 0.13);
  ctx.strokeStyle = o.outline ?? CSS.plumDeep;
  put(ctx.strokeText);

  ctx.fillStyle = o.fill ?? CSS.cream;
  put(ctx.fillText);

  if (o.highlight) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, y - o.size * 0.62, ctx.canvas.width, o.size * 0.42);
    ctx.clip();
    ctx.fillStyle = o.highlight;
    put(ctx.fillText);
    ctx.restore();
  }
  ctx.restore();
}

/**
 * Awning stripes running across the U axis.
 * @param {number} a First stripe colour.
 * @param {number} b Second stripe colour.
 * @param {number} [count] Stripe pairs across the texture.
 * @returns {THREE.CanvasTexture}
 */
export function stripes(a, b, count = 8) {
  return paint(`stripes-${a}-${b}-${count}`, 512, 128, (ctx, w, h) => {
    const bandW = w / count;
    for (let i = 0; i < count; i++) {
      ctx.fillStyle = css(i % 2 ? b : a);
      ctx.fillRect(i * bandW, 0, bandW + 1, h);
    }
    // A soft vertical gradient reads as canvas catching the light, which keeps
    // a flat-shaded awning from looking like coloured paper.
    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, 'rgba(255,255,255,0.16)');
    grad.addColorStop(0.45, 'rgba(255,255,255,0)');
    grad.addColorStop(1, 'rgba(0,0,0,0.20)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  });
}

/**
 * Painted timber. Grain runs along U.
 *
 * Pass `repeat` on anything more than about a metre across — a single texture
 * stretched over a 5 m booth floor reads as a smear, which is exactly what the
 * first render of this art pass looked like.
 *
 * @param {number} [tone]
 * @param {[number, number]} [repeat]
 * @returns {THREE.CanvasTexture}
 */
export function woodGrain(tone = ART.wood, repeat = null) {
  return paint(`wood-${tone}`, 512, 256, (ctx, w, h) => {
    const rnd = rngFor(`wood${tone}`);
    ctx.fillStyle = css(tone);
    ctx.fillRect(0, 0, w, h);

    for (let i = 0; i < 46; i++) {
      const y = rnd() * h;
      const amp = 2 + rnd() * 7;
      const dark = rnd() < 0.55;
      ctx.strokeStyle = dark
        ? `rgba(0,0,0,${0.05 + rnd() * 0.10})`
        : `rgba(255,255,255,${0.03 + rnd() * 0.07})`;
      ctx.lineWidth = 1 + rnd() * 2.6;
      ctx.beginPath();
      for (let x = 0; x <= w; x += 16) {
        const yy = y + Math.sin((x / w) * Math.PI * (1.5 + rnd() * 0.4) + i) * amp;
        if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
      }
      ctx.stroke();
    }
    // A few knots.
    for (let i = 0; i < 3; i++) {
      const x = rnd() * w, y = rnd() * h;
      const r = 4 + rnd() * 7;
      const g = ctx.createRadialGradient(x, y, 1, x, y, r);
      g.addColorStop(0, 'rgba(0,0,0,0.30)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }, { repeat });
}

/**
 * A scalloped valance: opaque body with semicircular bites out of the bottom.
 * Used as an alpha map so the awning skirt gets a real silhouette.
 * @param {number} [teeth]
 * @returns {THREE.CanvasTexture}
 */
export function scallopAlpha(teeth = 10) {
  return paint(`scallop-${teeth}`, 512, 128, (ctx, w, h) => {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h * 0.52);
    const step = w / teeth;
    for (let i = 0; i < teeth; i++) {
      ctx.beginPath();
      ctx.arc(step * (i + 0.5), h * 0.52, step * 0.5, 0, Math.PI);
      ctx.fill();
    }
  }, { data: true });
}

/**
 * A row of bulbs on a dark rail — the marquee edge treatment.
 * @param {number} [count]
 * @param {number} [rail]
 * @returns {THREE.CanvasTexture}
 */
export function bulbBand(count = 12, rail = ART.plumDeep) {
  return paint(`bulbband-${count}-${rail}`, 512, 64, (ctx, w, h) => {
    ctx.fillStyle = css(rail);
    ctx.fillRect(0, 0, w, h);
    const step = w / count;
    for (let i = 0; i < count; i++) {
      const x = step * (i + 0.5);
      const g = ctx.createRadialGradient(x, h / 2, 1, x, h / 2, h * 0.46);
      g.addColorStop(0, '#fffdf4');
      g.addColorStop(0.42, CSS.bulbWarm);
      g.addColorStop(1, 'rgba(255,200,120,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, h / 2, h * 0.46, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

/**
 * A wrapped product label — the "CHAOS COLA" treatment on a tin can.
 * @param {string} text
 * @param {{ bg?: number, band?: number, ink?: string, sub?: string }} [o]
 * @returns {THREE.CanvasTexture}
 */
export function canLabel(text = 'CHAOS COLA', o = {}) {
  const bg = o.bg ?? ART.red;
  const band = o.band ?? ART.cream;
  const key = `canlabel-${text}-${bg}-${band}-${o.sub || ''}`;
  return paint(key, 512, 256, (ctx, w, h) => {
    ctx.fillStyle = css(bg);
    ctx.fillRect(0, 0, w, h);

    // Cream band through the middle carries the wordmark.
    ctx.fillStyle = css(band);
    ctx.fillRect(0, h * 0.26, w, h * 0.48);
    ctx.fillStyle = 'rgba(0,0,0,0.13)';
    ctx.fillRect(0, h * 0.26, w, 5);
    ctx.fillRect(0, h * 0.74 - 5, w, 5);

    // The wordmark repeats twice so it is legible from any side of the can.
    const words = String(text).split(' ');
    for (const half of [0, 1]) {
      const cx = w * (0.25 + half * 0.5);
      if (words.length > 1) {
        showcardText(ctx, words[0], cx, h * 0.42, {
          size: 46, fill: css(bg), outline: 'rgba(0,0,0,0.35)',
        });
        showcardText(ctx, words.slice(1).join(' '), cx, h * 0.60, {
          size: 40, fill: css(ART.plumDeep), outline: 'rgba(0,0,0,0.2)',
        });
      } else {
        showcardText(ctx, words[0], cx, h * 0.5, {
          size: 52, fill: css(bg), outline: 'rgba(0,0,0,0.35)',
        });
      }
    }

    // Gold pinstripes top and bottom.
    ctx.fillStyle = css(ART.gold);
    ctx.fillRect(0, h * 0.13, w, 8);
    ctx.fillRect(0, h * 0.85, w, 8);
  });
}

/**
 * A radiating starburst — prize backdrops, golden-duck sparkle, marquee centres.
 * @param {number} [a] Ray colour.
 * @param {number} [b] Ground colour.
 * @param {number} [rays]
 * @returns {THREE.CanvasTexture}
 */
export function starburst(a = ART.gold, b = ART.red, rays = 16) {
  return paint(`starburst-${a}-${b}-${rays}`, 512, 512, (ctx, w, h) => {
    ctx.fillStyle = css(b);
    ctx.fillRect(0, 0, w, h);
    const cx = w / 2, cy = h / 2, r = w;
    ctx.fillStyle = css(a);
    for (let i = 0; i < rays; i++) {
      const a0 = (i / rays) * Math.PI * 2;
      const a1 = a0 + (Math.PI / rays);
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r);
      ctx.lineTo(cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
      ctx.closePath();
      ctx.fill();
    }
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.5);
    g.addColorStop(0, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.5, 'rgba(255,255,255,0.06)');
    g.addColorStop(1, 'rgba(0,0,0,0.22)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
}

/**
 * Baseball stitching — two curved arcs of red V-stitches on off-white.
 * @returns {THREE.CanvasTexture}
 */
export function baseballStitch() {
  return paint('baseball-stitch', 512, 256, (ctx, w, h) => {
    ctx.fillStyle = '#f7f4ea';
    ctx.fillRect(0, 0, w, h);

    // Faint leather mottle.
    const rnd = rngFor('baseball');
    for (let i = 0; i < 260; i++) {
      ctx.fillStyle = `rgba(0,0,0,${0.012 + rnd() * 0.02})`;
      ctx.beginPath();
      ctx.arc(rnd() * w, rnd() * h, 1 + rnd() * 3, 0, Math.PI * 2);
      ctx.fill();
    }

    const seam = (centreX) => {
      ctx.strokeStyle = '#c8362c';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      for (let i = 0; i <= 22; i++) {
        const t = i / 22;
        const y = t * h;
        const x = centreX + Math.sin(t * Math.PI) * 46 - 23;
        const tilt = 0.5 + Math.cos(t * Math.PI) * 0.4;
        ctx.beginPath();
        ctx.moveTo(x - 11, y - 7 * tilt);
        ctx.lineTo(x + 2, y);
        ctx.moveTo(x - 11, y + 7 * tilt);
        ctx.lineTo(x + 2, y);
        ctx.stroke();
      }
    };
    seam(w * 0.27);
    seam(w * 0.77);
  });
}

/**
 * Basketball pebbling with vertical seams.
 * @param {number} [base]
 * @returns {THREE.CanvasTexture}
 */
export function basketballSkin(base = 0xd9662c) {
  return paint(`bball-${base}`, 512, 256, (ctx, w, h) => {
    ctx.fillStyle = css(base);
    ctx.fillRect(0, 0, w, h);
    const rnd = rngFor('bball');
    for (let i = 0; i < 900; i++) {
      ctx.fillStyle = `rgba(0,0,0,${0.05 + rnd() * 0.08})`;
      ctx.beginPath();
      ctx.arc(rnd() * w, rnd() * h, 1.4 + rnd() * 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = '#2b1a12';
    ctx.lineWidth = 7;
    for (const x of [w * 0.25, w * 0.75]) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    // The horizontal seam bows, as the real ball's does.
    ctx.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const y = h * 0.5 + Math.sin((x / w) * Math.PI * 2) * h * 0.17;
      if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  });
}

/**
 * Coconut husk — fibrous brown noise. Doubles as a bump-ish colour break.
 * @returns {THREE.CanvasTexture}
 */
export function coconutHusk() {
  return paint('coconut-husk', 512, 256, (ctx, w, h) => {
    ctx.fillStyle = '#5c3a22';
    ctx.fillRect(0, 0, w, h);
    const rnd = rngFor('coconut');
    for (let i = 0; i < 1400; i++) {
      const x = rnd() * w, y = rnd() * h;
      const len = 6 + rnd() * 26;
      const ang = (rnd() - 0.5) * 0.9 + Math.PI / 2;
      const light = rnd() < 0.45;
      ctx.strokeStyle = light
        ? `rgba(190,150,110,${0.10 + rnd() * 0.22})`
        : `rgba(30,16,8,${0.10 + rnd() * 0.25})`;
      ctx.lineWidth = 0.8 + rnd() * 1.8;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(ang) * len, y + Math.sin(ang) * len);
      ctx.stroke();
    }
    // Three dark eyes clustered on one side, as a real coconut has.
    for (const [ex, ey] of [[0.2, 0.3], [0.26, 0.44], [0.14, 0.45]]) {
      const g = ctx.createRadialGradient(ex * w, ey * h, 1, ex * w, ey * h, 16);
      g.addColorStop(0, 'rgba(20,10,4,0.85)');
      g.addColorStop(1, 'rgba(20,10,4,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(ex * w, ey * h, 16, 0, Math.PI * 2);
      ctx.fill();
    }
  });
}

/**
 * A barber-pole spiral for posts and candy-striped rings.
 * @param {number} a
 * @param {number} b
 * @param {number} [turns]
 * @returns {THREE.CanvasTexture}
 */
export function barberStripe(a = ART.red, b = ART.cream, turns = 6, repeat = null) {
  return paint(`barber-${a}-${b}-${turns}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = css(b);
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = css(a);
    ctx.save();
    ctx.translate(w / 2, h / 2);
    ctx.rotate(-0.5);
    const band = h / turns;
    for (let i = -turns * 2; i < turns * 2; i++) {
      ctx.fillRect(-w, i * band, w * 2, band * 0.5);
    }
    ctx.restore();
  }, { repeat });
}

/**
 * The classic ADMIT ONE ticket face.
 * @returns {THREE.CanvasTexture}
 */
export function ticketFace() {
  return paint('ticket-face', 512, 256, (ctx, w, h) => {
    ctx.fillStyle = css(ART.gold);
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = css(ART.plumDeep);
    ctx.lineWidth = 10;
    ctx.strokeRect(16, 16, w - 32, h - 32);
    ctx.lineWidth = 3;
    ctx.strokeRect(34, 34, w - 68, h - 68);

    showcardText(ctx, 'ADMIT', w * 0.5, h * 0.38, {
      size: 62, fill: css(ART.plumDeep), outline: 'rgba(255,255,255,0.45)', shadow: false,
    });
    showcardText(ctx, 'ONE', w * 0.5, h * 0.66, {
      size: 74, fill: css(ART.red), outline: css(ART.plumDeep), shadow: false,
    });

    // Perforation dots down both ends.
    ctx.fillStyle = 'rgba(0,0,0,0.25)';
    for (let i = 0; i < 9; i++) {
      const y = (i + 0.5) * (h / 9);
      ctx.beginPath(); ctx.arc(8, y, 5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(w - 8, y, 5, 0, Math.PI * 2); ctx.fill();
    }
  });
}

/**
 * A coin face: goose head in relief on milled gold.
 * @param {number} [metal]
 * @returns {THREE.CanvasTexture}
 */
export function coinFace(metal = ART.gold) {
  return paint(`coin-${metal}`, 256, 256, (ctx, w, h) => {
    const cx = w / 2, cy = h / 2;
    const g = ctx.createRadialGradient(cx * 0.7, cy * 0.7, 4, cx, cy, w * 0.55);
    g.addColorStop(0, css(ART.goldLight));
    g.addColorStop(0.6, css(metal));
    g.addColorStop(1, css(ART.goldDeep));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    // Milled rim.
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.lineWidth = 8;
    ctx.beginPath(); ctx.arc(cx, cy, w * 0.42, 0, Math.PI * 2); ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.30)';
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(cx, cy, w * 0.36, 0, Math.PI * 2); ctx.stroke();

    // Goose head in profile, drawn as light-above / shadow-below so it reads
    // as embossed metal rather than a printed sticker.
    const emboss = (dx, dy, style) => {
      ctx.save();
      ctx.translate(dx, dy);
      ctx.fillStyle = style;
      ctx.beginPath();
      ctx.ellipse(cx - 6, cy - 18, 26, 30, -0.2, 0, Math.PI * 2);   // head
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx - 4, cy + 4);
      ctx.quadraticCurveTo(cx + 16, cy + 30, cx + 6, cy + 56);      // neck
      ctx.quadraticCurveTo(cx - 12, cy + 34, cx - 18, cy + 6);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx - 28, cy - 22);
      ctx.lineTo(cx - 62, cy - 12);
      ctx.lineTo(cx - 28, cy - 2);
      ctx.closePath();                                              // bill
      ctx.fill();
      ctx.restore();
    };
    emboss(-2.5, -2.5, 'rgba(255,255,255,0.42)');
    emboss(2.5, 2.5, 'rgba(90,55,10,0.45)');
    emboss(0, 0, css(metal));
  });
}

/**
 * Duck-target bullseye rosette.
 * @param {boolean} [golden]
 * @returns {THREE.CanvasTexture}
 */
export function bullseye(golden = false) {
  return paint(`bullseye-${golden}`, 256, 256, (ctx, w, h) => {
    const cx = w / 2, cy = h / 2;
    const rings = golden
      ? [ART.goldDeep, ART.cream, ART.gold, ART.cream, ART.red]
      : [ART.cream, ART.red, ART.cream, ART.red, ART.gold];
    for (let i = 0; i < rings.length; i++) {
      ctx.fillStyle = css(rings[i]);
      ctx.beginPath();
      ctx.arc(cx, cy, (w * 0.48) * (1 - i / rings.length), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.arc(cx, cy, w * 0.47, 0, Math.PI * 2); ctx.stroke();
  });
}

/**
 * Plush fabric — a soft mottle so a sphere does not read as plastic.
 * @param {number} base
 * @returns {THREE.CanvasTexture}
 */
export function plushFelt(base) {
  return paint(`felt-${base}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = css(base);
    ctx.fillRect(0, 0, w, h);
    const rnd = rngFor(`felt${base}`);
    for (let i = 0; i < 1100; i++) {
      const light = rnd() < 0.5;
      ctx.strokeStyle = light
        ? `rgba(255,255,255,${0.03 + rnd() * 0.08})`
        : `rgba(0,0,0,${0.03 + rnd() * 0.07})`;
      ctx.lineWidth = 1 + rnd();
      const x = rnd() * w, y = rnd() * h, a = rnd() * Math.PI;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * 5, y + Math.sin(a) * 5);
      ctx.stroke();
    }
  });
}

/**
 * Fish scales, optionally broken by patches (the spotted variant).
 * @param {number} base
 * @param {number|null} [patch]
 * @returns {THREE.CanvasTexture}
 */
export function fishScales(base, patch = null) {
  return paint(`scales-${base}-${patch}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = css(base);
    ctx.fillRect(0, 0, w, h);

    if (patch !== null) {
      const rnd = rngFor(`patch${base}`);
      ctx.fillStyle = css(patch);
      for (let i = 0; i < 7; i++) {
        ctx.beginPath();
        ctx.ellipse(rnd() * w, rnd() * h, 22 + rnd() * 34, 18 + rnd() * 26,
          rnd() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.strokeStyle = 'rgba(0,0,0,0.15)';
    ctx.lineWidth = 2;
    const r = 15;
    for (let row = 0; row * r * 0.7 < h + r; row++) {
      const y = row * r * 0.7;
      const off = (row % 2) * r;
      for (let x = -r; x < w + r; x += r * 2) {
        ctx.beginPath();
        ctx.arc(x + off, y, r, Math.PI * 0.15, Math.PI * 0.85);
        ctx.stroke();
      }
    }
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, 'rgba(255,255,255,0.28)');
    g.addColorStop(0.5, 'rgba(255,255,255,0)');
    g.addColorStop(1, 'rgba(0,0,0,0.22)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  });
}

/**
 * Bunting flags on a string, as an alpha-cut strip.
 * @param {number[]} colors
 * @returns {{ map: THREE.CanvasTexture, alpha: THREE.CanvasTexture }}
 */
export function buntingStrip(colors = [ART.red, ART.cream, ART.teal, ART.gold]) {
  const key = colors.join('-');
  const flags = 12;
  const map = paint(`bunting-${key}`, 512, 128, (ctx, w, h) => {
    ctx.fillStyle = css(ART.cream);
    ctx.fillRect(0, 0, w, h);
    const step = w / flags;
    for (let i = 0; i < flags; i++) {
      ctx.fillStyle = css(colors[i % colors.length]);
      ctx.fillRect(i * step, 0, step + 1, h);
    }
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ctx.fillRect(0, h * 0.82, w, h * 0.18);
  });
  const alpha = paint(`bunting-a-${flags}`, 512, 128, (ctx, w, h) => {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h * 0.1);          // the cord
    const step = w / flags;
    for (let i = 0; i < flags; i++) {         // triangular pennants
      ctx.beginPath();
      ctx.moveTo(i * step + step * 0.06, h * 0.08);
      ctx.lineTo(i * step + step * 0.94, h * 0.08);
      ctx.lineTo(i * step + step * 0.5, h * 0.94);
      ctx.closePath();
      ctx.fill();
    }
  }, { data: true });
  return { map, alpha };
}

/**
 * A back-wall paper: subtle diagonal carnival pattern so a flat plane reads as
 * decorated rather than blank.
 * @param {number} base
 * @param {number} ink
 * @returns {THREE.CanvasTexture}
 */
export function wallPattern(base = ART.cream, ink = ART.creamShade, repeat = null) {
  return paint(`wall-${base}-${ink}`, 256, 256, (ctx, w, h) => {
    ctx.fillStyle = css(base);
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = css(ink);
    ctx.lineWidth = 6;
    ctx.globalAlpha = 0.5;
    for (let i = -h; i < w + h; i += 34) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i + h, h);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    // Small diamonds in the gaps.
    ctx.fillStyle = css(ink);
    ctx.globalAlpha = 0.55;
    for (let y = 17; y < h; y += 34) {
      for (let x = 17; x < w; x += 34) {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.PI / 4);
        ctx.fillRect(-4, -4, 8, 8);
        ctx.restore();
      }
    }
    ctx.globalAlpha = 1;
  }, { repeat });
}

/** World size in metres of one `midwayDirt` tile. Its consumers author UVs in metres. */
export const MIDWAY_TILE_METRES = 4;

/**
 * Trodden fairground earth — the midway walkway and the plaza at its end.
 *
 * Deliberately very low contrast. This is background surface read at every
 * distance from underfoot to sixty metres, so anything with real bite in it
 * shimmers as the mip levels cross over. Every mark is stamped at each tile
 * offset it could bleed across, so the pattern repeats seamlessly.
 *
 * @param {number} [tone] Base earth colour.
 * @returns {THREE.CanvasTexture}
 */
export function midwayDirt(tone = 0xb9a689) {
  const repeat = [1 / MIDWAY_TILE_METRES, 1 / MIDWAY_TILE_METRES];
  return paint(`midway-dirt-${tone}`, 512, 512, (ctx, w, h) => {
    const rnd = rngFor(`midway${tone}`);
    ctx.fillStyle = css(tone);
    ctx.fillRect(0, 0, w, h);

    /** Stamp one mark at every tile offset it reaches across. */
    const tiled = (x, y, r, fn) => {
      const xs = x < r ? [0, w] : x > w - r ? [0, -w] : [0];
      const ys = y < r ? [0, h] : y > h - r ? [0, -h] : [0];
      for (const dx of xs) for (const dy of ys) fn(dx, dy);
    };

    // Broad patches — ground swept bare in places, dusty in others.
    for (let i = 0; i < 28; i++) {
      const x = rnd() * w, y = rnd() * h, r = 46 + rnd() * 108;
      const dark = rnd() < 0.55;
      const a = dark ? 0.030 + rnd() * 0.038 : 0.022 + rnd() * 0.030;
      const inner = dark ? `rgba(56,34,18,${a})` : `rgba(255,246,224,${a})`;
      tiled(x, y, r, (dx, dy) => {
        const g = ctx.createRadialGradient(x + dx, y + dy, r * 0.15, x + dx, y + dy, r);
        g.addColorStop(0, inner);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x + dx, y + dy, r, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Wear running along V, the strip's long axis — the line people walk.
    // Whole sine periods keep each one seamless end to end.
    for (let i = 0; i < 13; i++) {
      const x = rnd() * w;
      const amp = 4 + rnd() * 15;
      const cycles = 1 + Math.floor(rnd() * 3);
      const phase = rnd() * Math.PI * 2;
      const a = 0.024 + rnd() * 0.030;
      const lw = 7 + rnd() * 24;
      const stroke = rnd() < 0.6 ? `rgba(255,248,230,${a})` : `rgba(50,30,16,${a})`;
      tiled(x, h / 2, amp + lw, (dx) => {
        ctx.strokeStyle = stroke;
        ctx.lineWidth = lw;
        ctx.lineCap = 'round';
        ctx.beginPath();
        for (let y = 0; y <= h; y += 16) {
          const xx = x + dx + Math.sin((y / h) * Math.PI * 2 * cycles + phase) * amp;
          if (y === 0) ctx.moveTo(xx, y); else ctx.lineTo(xx, y);
        }
        ctx.stroke();
      });
    }

    // Grit.
    for (let i = 0; i < 620; i++) {
      ctx.fillStyle = rnd() < 0.6
        ? `rgba(46,28,14,${0.03 + rnd() * 0.045})`
        : `rgba(255,250,236,${0.025 + rnd() * 0.035})`;
      ctx.beginPath();
      ctx.arc(rnd() * w, rnd() * h, 1 + rnd() * 2.4, 0, Math.PI * 2);
      ctx.fill();
    }

    // Confetti trodden in. Sparse and small on purpose — a handful of colour
    // flecks reads as a fairground, any more reads as a rash at distance.
    const flecks = [ART.red, ART.teal, ART.gold, ART.cream, ART.plumLight];
    for (let i = 0; i < 30; i++) {
      const x = rnd() * w, y = rnd() * h;
      const fw = 3 + rnd() * 5, fh = 2 + rnd() * 2.5;
      const rot = rnd() * Math.PI;
      const fill = css(flecks[Math.floor(rnd() * flecks.length)]);
      const alpha = 0.16 + rnd() * 0.14;
      tiled(x, y, 8, (dx, dy) => {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.fillStyle = fill;
        ctx.translate(x + dx, y + dy);
        ctx.rotate(rot);
        ctx.fillRect(-fw / 2, -fh / 2, fw, fh);
        ctx.restore();
      });
    }
  }, { repeat });
}

/** Dispose every cached texture. Full teardown only. */
export function disposeTextures() {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}
